import { ColumnDef } from '@tanstack/react-table';

import { FormattedRequirements, JobTitle } from '@/types/JobTitle';
import { removeTimeZone } from '@/utils/formatDate';

import { TableCellWithShowMore } from './TabelCellWithShowMore';
import TableCellAllocation from './TableCellAllocation';
import TableCellDate from './TableCellDate';
import TableData from './TableData';

export const JobTitleColumns: Array<ColumnDef<JobTitle>> = [
	{
		accessorKey: 'version',
		header: 'Versão',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[10rem] uppercase text-center',
		},
		cell: (props) => {
			return (
				<div className="flex w-full items-center justify-center">
					<TableCellAllocation
						text={props.getValue() as string}
						width="12rem"
						containerClassName="justify-center"
					/>
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'createdAt',
		header: 'Data de alteração',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[10rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[5rem]">
					<TableCellDate
						date={removeTimeZone(new Date(props.getValue() as string))}
						className="text-[11px]"
					/>
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'title',
		header: 'Título do Cargo',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[12rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[12rem]">
					<TableData
						tableValue={props.getValue() as string}
						className="text-[11px]"
					/>
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'seniority',
		header: 'Nível',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[8rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[4rem]">
					<TableData
						tableValue={props.getValue() as string}
						width="4rem"
						className="text-[11px]"
					/>
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'boards',
		header: 'Diretoria',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[15rem] uppercase',
		},
		cell: (props) => {
			const boards = props.getValue() as Array<string>;
			return (
				<div className="w-[12rem]">
					<TableCellAllocation text={boards.join('; ')} width="12rem" />
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'managements',
		header: 'Gerência',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[15rem] uppercase',
		},
		cell: (props) => {
			const managements = props.getValue() as Array<string>;

			return (
				<div className="w-[12rem]">
					<TableCellAllocation text={managements.join('; ')} width="12rem" />
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'workStations',
		header: 'Local de Trabalho',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[15rem] uppercase',
		},
		cell: (props) => {
			const workStation = (
				props.getValue() as Array<{
					gheCode: string;
					workStation: string;
				}>
			).map((it) => it.workStation);
			return (
				<div className="w-[12rem]">
					<TableCellAllocation text={workStation.join('; ')} width="12rem" />
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'objective',
		header: 'Objetivo do cargo',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[20rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[25rem] pr-4 text-[11px] text-[#4A4A4A]">
					{props.getValue() ? (
						<TableCellAllocation
							text={props.getValue() as string}
							width="17rem"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'responsibilities',
		header: 'Funções e responsabilidades',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[20rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[25rem] pr-4">
					{props.getValue() ? (
						<TableCellWithShowMore
							text={props.getValue() as string}
							title="Funções e responsabilidades"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'experience',
		header: 'Experiência',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[20rem] uppercase',
		},
		cell: (props) => {
			return (
				<div className="w-[25rem]">
					{props.getValue() ? (
						<TableCellWithShowMore
							text={props.getValue() as string}
							title="Experiência"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Requisitos educacionais obrigatórios',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[17rem] uppercase',
		},
		cell: (props) => {
			const educationalRequirements = (
				props.getValue() as FormattedRequirements
			).mandatory.educational;

			return (
				<div className="w-[15rem] text-[11px] text-[#4A4A4A]">
					{props.getValue() && educationalRequirements.length ? (
						<TableCellWithShowMore
							title="Requisitos educacionais obrigatórios"
							text={educationalRequirements.sort().join('; ')}
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Requisitos educacionais desejáveis',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[16rem] uppercase',
		},
		cell: (props) => {
			const educationalRequirements = (
				props.getValue() as FormattedRequirements
			).optional.educational;
			return (
				<div className="w-[15rem] text-[11px] text-[#4A4A4A]">
					{props.getValue() && educationalRequirements.length ? (
						<TableCellWithShowMore
							text={educationalRequirements.sort().join('; ')}
							title="Requisitos educacionais desejáveis"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Requisitos profissionais obrigatórios',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[17rem] uppercase',
		},
		cell: (props) => {
			const professionalRequirements = (
				props.getValue() as FormattedRequirements
			).mandatory.professional;

			return (
				<div className="w-[15rem]">
					{props.getValue() && professionalRequirements.length ? (
						<TableCellWithShowMore
							text={professionalRequirements.sort().join('; ')}
							title="Requisitos profissionais obrigatórios"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Requisitos profissionais desejáveis',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[16rem] uppercase',
		},
		cell: (props) => {
			const professionalRequirements = (
				props.getValue() as FormattedRequirements
			).optional.professional;

			return (
				<div className="w-[15rem]">
					{props.getValue() && professionalRequirements.length ? (
						<TableCellWithShowMore
							text={professionalRequirements.sort().join('; ')}
							title="Requisitos profissionais desejáveis"
							className="text-[11px]"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Outros requisitos obrigatórios',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[15rem] uppercase',
		},
		cell: (props) => {
			const otherRequirements = (props.getValue() as FormattedRequirements)
				.mandatory.other;

			return (
				<div className="flex w-[15rem] justify-center break-all text-[11px] text-[#4A4A4A]">
					{props.getValue() && otherRequirements.length ? (
						<TableCellWithShowMore
							text={otherRequirements.sort().join('; ')}
							title="Outros requisitos obrigatórios"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'formattedRequirements',
		header: 'Outros requisitos desejáveis',
		meta: {
			headerClassName: 'pr-2 text-xs min-w-[15rem] uppercase',
		},
		cell: (props) => {
			const otherRequirements = (props.getValue() as FormattedRequirements)
				.optional.other;

			return (
				<div className="flex w-[15rem] justify-center break-all text-[11px] text-[#4A4A4A]">
					{props.getValue() && otherRequirements.length ? (
						<TableCellWithShowMore
							text={otherRequirements.sort().join('; ')}
							title="Outros requisitos desejáveis"
						/>
					) : (
						<>-</>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
];
